import { RefObject, useLayoutEffect, useState } from 'react';

import { TPanelContainer } from './types';

/**
 * Где искать контейнер, если его не задали.
 *
 * `-container` — модалка в antd 6, `-content` оставлен для более старой разметки:
 * класс переименовали, и жёсткая привязка к одному имени молча роняет панель на
 * весь экран вместо модалки.
 */
const DEFAULT_CONTAINER_SELECTOR = '.ant-modal-container, .ant-modal-content, .ant-modal';

/** Зазор между панелью и полем, чтобы они не слипались. */
const PANEL_GAP = 8;

/** Уже панель не бывает: иначе названия вариантов не прочитать. */
const MIN_PANEL_WIDTH = 160;

export type TPanelPosition = {
    /** Куда рендерить панель порталом. */
    container: HTMLElement | null;
    style: React.CSSProperties;
};

const resolveContainer = (trigger: HTMLElement, source?: TPanelContainer): HTMLElement | null => {
    if (typeof source === 'function') return source();
    if (typeof source === 'string') return trigger.closest<HTMLElement>(source);
    if (source) return source;

    return trigger.closest<HTMLElement>(DEFAULT_CONTAINER_SELECTOR);
};

/**
 * Растягивает панель вариантов по всей высоте контейнера, слева от поля.
 *
 * Панель рендерится порталом в контейнер, а не рядом с полем: внутри карточки
 * характеристик её обрезал бы `overflow`, и чем ниже поле, тем короче был бы
 * список. Правый край считаем по полю — панель занимает всё место слева от него,
 * то есть колонку с подписями характеристик.
 *
 * Границы панели — это границы контейнера, за них она не выходит. Если контейнер
 * не найден, растягиваемся по окну.
 */
export const usePanelPosition = (
    triggerRef: RefObject<HTMLElement>,
    isOpen: boolean,
    panelContainer?: TPanelContainer
): TPanelPosition => {
    const [position, setPosition] = useState<TPanelPosition>({ container: null, style: {} });

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const measure = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;

            const container = resolveContainer(trigger, panelContainer);
            const bounds = container
                ? container.getBoundingClientRect()
                : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
            const triggerRect = trigger.getBoundingClientRect();

            // Абсолютные координаты считаются от контейнера, поэтому ему нужен
            // собственный контекст позиционирования — иначе панель отсчитается
            // от более далёкого предка и уедет за пределы модалки
            if (container && getComputedStyle(container).position === 'static') {
                container.style.position = 'relative';
            }

            const rightOffset = bounds.right - triggerRect.left + PANEL_GAP;
            const available = bounds.width - rightOffset;

            setPosition({
                container: container ?? document.body,
                style: {
                    position: container ? 'absolute' : 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    // если поле занимает почти всю ширину, оставляем панели минимум
                    right:
                        available < MIN_PANEL_WIDTH
                            ? Math.max(bounds.width - MIN_PANEL_WIDTH, 0)
                            : rightOffset,
                },
            });
        };

        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [triggerRef, isOpen, panelContainer]);

    return position;
};
